/**
 * Feedback Inbox — admin-only Type 1 Standard surface at /admin/feedback.
 *
 * Day 62b first real arbi-dev surface, replacing the Day 62a Placeholder.
 * Mirrors src/pages/Tasks.tsx structurally (toolbar shape, filter group
 * pattern, slide-in detail panel, AG Grid Infinite Row Model config) and
 * diverges on three documented points:
 *
 *   1. Datasource — PostgREST direct via supabase.from('feedback_items')
 *      with { count: 'exact' } and .range(...) per EXTENDED_BUILD_PLAN
 *      Day 62b spec line 416. No get_feedback_items RPC. Bounded V1
 *      volume (<100 rows V1, <1k V2) makes RPC overhead unjustified.
 *
 *   2. Deep-link ?id=<uuid> consumer — fetches one row via maybeSingle,
 *      opens detail panel pre-populated, strips param so refresh is
 *      idempotent. Mirrors PODetail.tsx's setSearchParams strip pattern.
 *      404 path toasts "Feedback item not found" and clears.
 *
 *   3. Document title — useDocumentTitle hook (Day 62b fold-in per
 *      BACKLOG line 1178) sets per-page tab title. arbi-dev only.
 *
 * Triage shape: detail panel surfaces 4 status transitions
 * (Reviewed / Shipped / Declined / Reopen) via triage_feedback RPC.
 * Bulk-action bar surfaces 2 (Mark Reviewed, Decline) via
 * bulk_triage_feedback RPC. Both RPCs write status + triaged_at +
 * triaged_by atomically to satisfy feedback_items_triage_invariant.
 *
 * No Export button V1 — feedback volume bounded, low-utility for an
 * admin triage surface. Filed as Day 62b follow-up if Sol asks.
 */

import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search, CheckCircle, X, RotateCcw, Send, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { reportDatasourceFailure } from '../lib/agGridErrorTracker';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Local types ───────────────────────────────────────────────────────────────

type FeedbackStatus = 'open' | 'reviewed' | 'shipped' | 'declined';
type FeedbackType = 'bug' | 'feature' | 'other';
type FeedbackImportance = 'low' | 'medium' | 'high' | 'critical';

interface FeedbackRow {
  id: string;
  submitted_by: string | null;
  type: FeedbackType | string;
  importance: FeedbackImportance | string;
  title: string;
  description: string;
  screenshot_path: string | null;
  current_route: string | null;
  user_agent: string | null;
  app_version: string | null;
  status: FeedbackStatus | string;
  submission_method: string;
  triaged_by: string | null;
  triaged_at: string | null;
  triage_note: string | null;
  created_at: string;
  updated_at: string;
}

interface ToastState {
  type: 'success' | 'error';
  message: string;
}

type GenericSupabaseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// ── Filter constants ──────────────────────────────────────────────────────────

const STATUS_FILTERS: ReadonlyArray<{ id: FeedbackStatus | null; label: string }> = [
  { id: null, label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'reviewed', label: 'Reviewed' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'declined', label: 'Declined' },
] as const;

const TYPE_FILTERS: ReadonlyArray<{ id: FeedbackType | null; label: string }> = [
  { id: null, label: 'All' },
  { id: 'bug', label: 'Bug' },
  { id: 'feature', label: 'Feature' },
  { id: 'other', label: 'Other' },
] as const;

const IMPORTANCE_FILTERS: ReadonlyArray<{ id: FeedbackImportance | null; label: string }> = [
  { id: null, label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
] as const;

// ── Badge components ──────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }): JSX.Element {
  const cls: string =
    (
      {
        open: 'bg-info/15 text-info',
        reviewed: 'bg-warning/15 text-warning',
        shipped: 'bg-success/15 text-success',
        declined: 'bg-surface-3 text-ink-muted',
      } as Record<string, string>
    )[status] || 'bg-surface-3 text-ink-muted';
  const label: string =
    (
      {
        open: 'Open',
        reviewed: 'Reviewed',
        shipped: 'Shipped',
        declined: 'Declined',
      } as Record<string, string>
    )[status] || status;
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', cls)}>
      {label}
    </span>
  );
}

function TypeBadge({ feedbackType }: { feedbackType: string }): JSX.Element {
  const cls: string =
    (
      {
        bug: 'bg-danger/15 text-danger',
        feature: 'bg-accent/15 text-accent',
        other: 'bg-surface-3 text-ink-secondary',
      } as Record<string, string>
    )[feedbackType] || 'bg-surface-3 text-ink-muted';
  const label: string =
    (
      { bug: 'Bug', feature: 'Feature', other: 'Other' } as Record<string, string>
    )[feedbackType] || feedbackType;
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', cls)}>
      {label}
    </span>
  );
}

function ImportanceBadge({ importance }: { importance: string }): JSX.Element {
  const cls: string =
    (
      {
        critical: 'bg-danger/15 text-danger border border-danger/30',
        high: 'bg-warning/15 text-warning border border-warning/30',
        medium: 'bg-info/15 text-info border border-info/30',
        low: 'bg-surface-3 text-ink-muted border border-border',
      } as Record<string, string>
    )[importance] || 'bg-surface-3 text-ink-muted';
  const label: string =
    (
      {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
      } as Record<string, string>
    )[importance] || importance;
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', cls)}>
      {label}
    </span>
  );
}

// ── Relative time ─────────────────────────────────────────────────────────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Filter pill component ─────────────────────────────────────────────────────

interface FilterGroupProps<T extends string | null> {
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
}

function FilterGroup<T extends string | null>({
  options,
  value,
  onChange,
}: FilterGroupProps<T>): JSX.Element {
  return (
    <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
      {options.map((f) => (
        <button
          key={f.id ?? 'all'}
          onClick={() => onChange(f.id)}
          aria-pressed={value === f.id}
          className={clsx(
            'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer whitespace-nowrap',
            value === f.id
              ? 'bg-surface-4 text-ink-bright'
              : 'text-ink-muted hover:text-ink-secondary'
          )}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────

interface ToastProps {
  toast: ToastState | null;
  onDismiss: () => void;
}

function Toast({ toast, onDismiss }: ToastProps): JSX.Element | null {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);
  if (!toast) return null;
  const bg =
    toast.type === 'success'
      ? 'bg-success/15 border-success/30 text-success'
      : 'bg-danger/15 border-danger/30 text-danger';
  return (
    <div
      className={clsx(
        'fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm font-medium shadow-lg',
        bg
      )}
    >
      {toast.message}
    </div>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────

interface FeedbackDetailPanelProps {
  row: FeedbackRow | null;
  onClose: () => void;
  onTriage: (id: string, status: FeedbackStatus, note: string | null) => void;
}

function FeedbackDetailPanel({
  row,
  onClose,
  onTriage,
}: FeedbackDetailPanelProps): JSX.Element | null {
  const [note, setNote] = useState<string>('');

  // Reset the note input when the panel switches to a different row.
  useEffect(() => {
    setNote('');
  }, [row?.id]);

  if (!row) return null;

  const showReopen = row.status !== 'open';

  return (
    <div className="w-80 flex-shrink-0 bg-surface-1 border-l border-border h-full overflow-y-auto pr-2.5">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-ink-bright truncate pr-2">{row.title}</h3>
        <button
          onClick={onClose}
          className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          aria-label="Close panel"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <ImportanceBadge importance={row.importance} />
          <TypeBadge feedbackType={row.type} />
          <StatusBadge status={row.status} />
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Description</p>
          <p className="text-xs text-ink-primary whitespace-pre-wrap">{row.description}</p>
        </div>

        {row.current_route && (
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">From route</p>
            <p className="text-xs font-mono text-ink-secondary break-all">{row.current_route}</p>
          </div>
        )}

        {row.app_version && (
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">App version</p>
            <p className="text-xs font-mono text-ink-muted">{row.app_version}</p>
          </div>
        )}

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Submitted</p>
          <p className="text-xs font-mono text-ink-muted">{relativeTime(row.created_at)}</p>
        </div>

        {row.triaged_at && (
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Triaged</p>
            <p className="text-xs font-mono text-ink-muted">{relativeTime(row.triaged_at)}</p>
          </div>
        )}

        {row.triage_note && (
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Triage note</p>
            <p className="text-xs text-ink-primary whitespace-pre-wrap">{row.triage_note}</p>
          </div>
        )}

        <div className="border-t border-border-subtle pt-3 space-y-2">
          <label
            htmlFor="triage-note"
            className="text-2xs uppercase tracking-wider text-ink-muted block"
          >
            Add triage note (optional)
          </label>
          <textarea
            id="triage-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why this status? (optional)"
            rows={3}
            className="arbi-input w-full text-xs resize-none"
          />

          <button
            onClick={() => onTriage(row.id, 'reviewed', note.trim() || null)}
            className="arbi-btn-ghost text-xs w-full justify-center"
          >
            <CheckCircle size={12} /> Mark Reviewed
          </button>
          <button
            onClick={() => onTriage(row.id, 'shipped', note.trim() || null)}
            className="arbi-btn-primary text-xs w-full justify-center"
          >
            <Send size={12} /> Mark Shipped
          </button>
          <button
            onClick={() => onTriage(row.id, 'declined', note.trim() || null)}
            className="arbi-btn-ghost text-xs w-full justify-center text-danger"
          >
            <XCircle size={12} /> Decline
          </button>
          {showReopen && (
            <button
              onClick={() => onTriage(row.id, 'open', note.trim() || null)}
              className="arbi-btn-ghost text-xs w-full justify-center"
            >
              <RotateCcw size={12} /> Reopen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function FeedbackInbox(): JSX.Element {
  useDocumentTitle('Feedback Inbox');

  const gridRef = useRef<AgGridReact<FeedbackRow> | null>(null);
  const gridApiRef = useRef<GridApi<FeedbackRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | null>(null);
  const [typeFilter, setTypeFilter] = useState<FeedbackType | null>(null);
  const [importanceFilter, setImportanceFilter] = useState<FeedbackImportance | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedRows, setSelectedRows] = useState<FeedbackRow[]>([]);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [detailRow, setDetailRow] = useState<FeedbackRow | null>(null);
  const [confirmDecline, setConfirmDecline] = useState<boolean>(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const showToast = useCallback((t: ToastState): void => setToast(t), []);
  const hasFilters = Boolean(
    statusFilter || typeFilter || importanceFilter || searchText.trim()
  );

  // ── State ref for getRowsFn ───────────────────────────────────
  const stateRef = useRef<{
    searchText: string;
    statusFilter: FeedbackStatus | null;
    typeFilter: FeedbackType | null;
    importanceFilter: FeedbackImportance | null;
  }>({ searchText, statusFilter, typeFilter, importanceFilter });
  stateRef.current = { searchText, statusFilter, typeFilter, importanceFilter };

  // ── Datasource — PostgREST direct ─────────────────────────────
  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const {
      searchText: st,
      statusFilter: sf,
      typeFilter: tf,
      importanceFilter: imf,
    } = stateRef.current;

    try {
      let query = supabase
        .from('feedback_items')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (sf) query = query.eq('status', sf);
      if (tf) query = query.eq('type', tf);
      if (imf) query = query.eq('importance', imf);
      if (st.trim()) {
        // PostgREST .ilike treats % and _ as wildcards — escape user
        // input so a literal % in the search box doesn't match all rows.
        const term = st.trim().replace(/[%_]/g, (m) => `\\${m}`);
        query = query.or(`title.ilike.%${term}%,description.ilike.%${term}%`);
      }

      // IGetRowsParams.endRow is exclusive; PostgREST .range is inclusive.
      const { data, error, count } = await query.range(startRow, endRow - 1);
      if (error) throw new Error(error.message);

      const rows = (data ?? []) as FeedbackRow[];
      const total = count ?? 0;
      setTotalCount(total);
      successCallback(rows, total);
    } catch (e) {
      reportDatasourceFailure(
        'FeedbackInbox',
        { rpcName: 'feedback_items', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<FeedbackRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  // Refresh grid when filters change
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, statusFilter, typeFilter, importanceFilter, getRowsFn]);

  const refreshGrid = useCallback((): void => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [getRowsFn]);

  // Selection tracking
  const onSelectionChanged = useCallback((): void => {
    if (!gridApiRef.current) return;
    setSelectedRows(gridApiRef.current.getSelectedRows());
  }, []);

  // ── Deep-link ?id=<uuid> consumer ─────────────────────────────
  // Mount-only effect (and re-fires on searchParams change). Strips
  // the param after consume so refresh is idempotent — mirrors
  // PODetail.tsx's setSearchParams strip pattern.
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) return;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('feedback_items')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (cancelled) return;
      if (error || !data) {
        showToast({ type: 'error', message: 'Feedback item not found' });
      } else {
        setDetailRow(data as FeedbackRow);
      }
      // Strip either way (success or 404) — refresh is idempotent.
      setSearchParams({}, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, showToast]);

  // ── Triage ────────────────────────────────────────────────────
  async function triageOne(
    id: string,
    status: FeedbackStatus,
    note: string | null
  ): Promise<void> {
    const { data, error } = await callRpc('triage_feedback', {
      p_id: id,
      p_status: status,
      p_triage_note: note,
    });
    if (error) {
      showToast({ type: 'error', message: error.message });
      return;
    }
    void data;
    showToast({ type: 'success', message: `Feedback marked ${status}` });
    setDetailRow(null);
    refreshGrid();
  }

  async function bulkTriage(status: FeedbackStatus): Promise<void> {
    const ids = selectedRows.map((r) => r.id);
    const { error } = await callRpc('bulk_triage_feedback', {
      p_ids: ids,
      p_status: status,
    });
    if (error) {
      showToast({ type: 'error', message: error.message });
      return;
    }
    showToast({
      type: 'success',
      message: `${ids.length} item(s) marked ${status}`,
    });
    setSelectedRows([]);
    refreshGrid();
  }

  // ── Cell renderers ────────────────────────────────────────────
  const ImportanceCell = useCallback(
    (p: ICellRendererParams<FeedbackRow>): JSX.Element | null => {
      if (!p.data) return null;
      return <ImportanceBadge importance={p.data.importance} />;
    },
    []
  );

  const TypeCell = useCallback((p: ICellRendererParams<FeedbackRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <TypeBadge feedbackType={p.data.type} />;
  }, []);

  const StatusCell = useCallback((p: ICellRendererParams<FeedbackRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <StatusBadge status={p.data.status} />;
  }, []);

  const SubmittedCell = useCallback(
    (p: ICellRendererParams<FeedbackRow>): JSX.Element | null => {
      if (!p.data) return null;
      return (
        <span className="font-mono text-xs text-ink-muted">{relativeTime(p.data.created_at)}</span>
      );
    },
    []
  );

  // ── Column definitions ────────────────────────────────────────
  const columnDefs = useMemo<ColDef<FeedbackRow>[]>(
    () =>
      [
        {
          suppressMovable: true,
          resizable: false,
          width: 40,
          maxWidth: 40,
          sortable: false,
          filter: false,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          colId: 'feedback_id',
          headerName: 'ID',
          field: 'id',
          suppressMovable: true,
          resizable: false,
          width: 80,
          minWidth: 80,
          pinned: 'left',
          sortable: false,
          cellStyle: { display: 'flex', alignItems: 'center' },
          valueFormatter: (p: ValueFormatterParams<FeedbackRow>) =>
            p.value ? '#' + String(p.value).slice(-6) : '—',
          cellClass: 'font-mono text-xs text-ink-muted',
        },
        {
          headerName: 'Importance',
          field: 'importance',
          width: 110,
          sortable: false,
          cellRenderer: ImportanceCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Title',
          field: 'title',
          flex: 1,
          minWidth: 220,
          sortable: false,
          cellStyle: { display: 'flex', alignItems: 'center', color: 'var(--ink-primary)' },
          tooltipField: 'title',
        },
        {
          headerName: 'Type',
          field: 'type',
          width: 100,
          sortable: false,
          cellRenderer: TypeCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 110,
          sortable: false,
          cellRenderer: StatusCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Submitted',
          field: 'created_at',
          width: 110,
          sortable: false,
          cellRenderer: SubmittedCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
      ] as ColDef<FeedbackRow>[],
    [ImportanceCell, TypeCell, StatusCell, SubmittedCell]
  );

  const defaultColDef = useMemo<ColDef<FeedbackRow>>(
    () => ({
      sortable: false,
      resizable: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerFontSize: '10px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback((event: RowClickedEvent<FeedbackRow>): void => {
    if (!event.data) return;
    const domEvent = event.event as Event | undefined;
    const target = domEvent?.target as HTMLElement | null;
    if (target?.closest('button') || target?.closest('.ag-checkbox-input')) return;
    setDetailRow(event.data);
  }, []);

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <ConfirmDialog
        open={confirmDecline}
        title="Decline selected feedback?"
        message={`This will mark ${selectedRows.length} item(s) as declined. They can be reopened later.`}
        confirmLabel="Decline"
        onConfirm={() => {
          void bulkTriage('declined');
          setConfirmDecline(false);
        }}
        onCancel={() => setConfirmDecline(false)}
      />

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Feedback Inbox</h1>
            <span className="text-xs text-ink-muted font-mono">
              {totalCount === 1 ? '1 item' : `${totalCount.toLocaleString()} items`}
            </span>
          </div>
        </div>

        {selectedRows.length > 0 ? (
          <div className="flex items-center gap-3">
            <span className="text-xs text-ink-secondary font-medium">
              {selectedRows.length} item(s) selected
            </span>
            <button
              onClick={() => bulkTriage('reviewed')}
              className="arbi-btn-ghost text-xs px-2 py-1"
            >
              <CheckCircle size={12} /> Mark Reviewed
            </button>
            <button
              onClick={() => setConfirmDecline(true)}
              className="arbi-btn-ghost text-xs px-2 py-1 text-danger"
            >
              <XCircle size={12} /> Decline
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <FilterGroup options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
            <FilterGroup options={TYPE_FILTERS} value={typeFilter} onChange={setTypeFilter} />
            <FilterGroup
              options={IMPORTANCE_FILTERS}
              value={importanceFilter}
              onChange={setImportanceFilter}
            />
            <div className="relative ml-auto">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
              />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search feedback..."
                aria-label="Search feedback"
                className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
              />
            </div>
          </div>
        )}
      </div>

      {/* Grid + optional detail panel */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        <div className="flex-1 p-2" style={{ minHeight: 0 }}>
          <AgGridReact
            ref={gridRef}
            theme={gridTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="infinite"
            cacheBlockSize={200}
            maxBlocksInCache={50}
            rowSelection={{
              mode: 'multiRow',
              enableClickSelection: false,
              checkboxes: true,
              headerCheckbox: false,
            }}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            onSelectionChanged={onSelectionChanged}
            animateRows={false}
            headerHeight={36}
            rowHeight={42}
            suppressCellFocus={true}
            suppressHorizontalScroll={true}
            overlayNoRowsTemplate={
              hasFilters
                ? '<span style="color:var(--ink-muted);font-size:13px">No feedback matches your filters</span>'
                : '<span style="color:var(--ink-muted);font-size:13px">No feedback yet — submissions arrive via the arbi Help overlay</span>'
            }
          />
        </div>

        {detailRow && (
          <FeedbackDetailPanel
            row={detailRow}
            onClose={() => setDetailRow(null)}
            onTriage={triageOne}
          />
        )}
      </div>
    </div>
  );
}
